import {useState, useEffect, useCallback, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {Form, Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {Button, ButtonWithLoader} from '../common/Button';
import Error from '../common/Error';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import {
  CallSheetEdit,
  CallSheetListProps,
  MatchParams,
  OptionsProp
} from '../../constants/types';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import ActionButtons from '../common/layout/ActionButtons';
import Spinner from '../common/Spinner';
import {callSheetValidationSchema} from '../../constants/validationSchemes';
import Container from '../common/layout/Container';
import {getCallSheetById, getLatestSchedulingProcess, updateCallSheetById} from '../../api/callsheet';
import {getDoctors} from '../../api/doctors';
import {convertDateFormat} from './helpers';
import {getAllTreatments} from '../../api/treatments';
import {getOperatoriesForFilters} from '../../api/operatories';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {
  TextField,
  CheckboxField,
  AutocompleteField
} from '../common/formik/fields';
import callSheetsRoutes from './routes';
import patientsRoutes from '../patients/routes';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {FormGroup, IconButton} from '@mui/material';
import {useSnackbarHook} from '../../hooks/useSnackbar';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {getSubprocessPopupMessage, getSubprocessToastMessage, processesNames, Subprocess} from '../../constants/constants';
import {LatestSchedulingProcessProps, ProcessCode} from '../processes/types';
import {startSubProcess} from '../../api/processes';
import {PopupDataProps} from './types';
import {ProcessType} from '../../api/settings/alert_alarm';

const doctorsQuery = {
  direction: 'asc',
  page: '1',
  per_page: '1000000'
};

type LocationState = {
  callSheetsPathMemo?: Nullable<string>,
  newDoctorId?: Nullable<number>
}

enum Field {
  SENECA_PATIENT_ID = 'senecaPatientId',
  FIRST_NAME = 'patientFirstName',
  LAST_NAME = 'patientLastName',
  PATIENT_ID = 'patientId',
  DOCTOR_ID = 'doctorId',
  APPOINTMENT_ID = 'appointmentId',
  TREATMENT_ID = 'treatmentId',
  APPOINTMENT_DATE = 'appointmentDate',
  WANTS_SOONER = 'wantsSooner',
  OPERATORY_ID = 'operatoryId',
  NEW_REFERRING_DOCTOR = 'newReferringDoctor'
}

const EditCallSheet = () => {
  const {t} = useTranslation('callSheets');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {params}: MatchParams = useRouteMatch();
  const {id} = params || {};
  const {state}: {state: LocationState} = useLocation();
  const {newDoctorId} = state || {newDoctorId: null};
  const {callSheetsPathMemo} = state || {callSheetsPathMemo: null};
  const [popupData, setPopupData] = useState<Nullable<PopupDataProps>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();
  const [latestSchedulingProcess, setLatestSchedulingProcess] =
    useState<Nullable<LatestSchedulingProcessProps>>(null);
  const [isRescheduleProcessing, setIsRescheduleProcessing] = useState(false);

  const redirectToDoctors = {
    path: `/configuration/referring-doctors`,
    from: callSheetsRoutes.item(id)
  };

  const {permissions: currentUserPermissions} = useAppSelector((state) =>
    state.permissions);
  const hasEditPermission = permissionsInfo.calls.edit.some((permission) =>
    currentUserPermissions.includes(permission));
  const hasDoctorsViewPermission =
    permissionsInfo.doctors.view.some((permission) =>
      currentUserPermissions.includes(permission));
  const [callSheetData, setCallSheetData] =
    useState<CallSheetListProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [doctorOptions, setDoctorOptions] =
    useState<OptionsProp[]>([]);
  const [treatmentOptions, setTreatmentOptions] =
    useState<OptionsProp[]>([]);
  const [operatoriesOptions, setOperatoriesOptions] =
    useState<OptionsProp[]>([]);
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);

  const clearMessages = () => {
    setError('');
    setApiValidationError(null);
  };

  const getAllData = async () => {
    setIsLoading(true);

    try {
      const callSheetResponse = await getCallSheetById(id);

      const getDoctorsList = () =>
      hasDoctorsViewPermission ?
        getDoctors(doctorsQuery) : null;
      const getTreatmentsList = () =>
        callSheetResponse?.appointment ?
          getAllTreatments() : null;
      const getOperatoriesList = () =>
        callSheetResponse?.appointment ?
          getOperatoriesForFilters() : null;

      const [
        doctorsResponse,
        treatmentsResponse,
        operatoriesResponse,
        latestSchedulingProcessResponse
      ] = await Promise.all([
        getDoctorsList(),
        getTreatmentsList(),
        getOperatoriesList(),
        getLatestSchedulingProcess(id)
      ]);

      setLatestSchedulingProcess(latestSchedulingProcessResponse);
      setCallSheetData(callSheetResponse);

      if (doctorsResponse?.content?.length) {
        setDoctorOptions(
            doctorsResponse.content.map(({id, firstName, lastName}) => ({
              value: id,
              label: [lastName, firstName].join(', ')
            })));
      }

      if (treatmentsResponse?.length) {
        setTreatmentOptions(
            treatmentsResponse.map(({rootId, displayName}) => ({
              value: rootId,
              label: displayName
            })));
      }

      if (operatoriesResponse?.length) {
        setOperatoriesOptions(
            operatoriesResponse.map(({id, number}) => ({
              value: id,
              label: number?.toString()
            })));
      }
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  const {patient, doctor, appointment} = callSheetData || {};

  const initialValues: CallSheetEdit = {
    [Field.SENECA_PATIENT_ID]: patient?.id || 0,
    [Field.FIRST_NAME]: patient?.firstName || '',
    [Field.LAST_NAME]: patient?.lastName || '',
    [Field.PATIENT_ID]: patient?.patientId || '',
    [Field.DOCTOR_ID]: doctor?.id || '',
    [Field.APPOINTMENT_ID]: appointment?.id,
    [Field.TREATMENT_ID]: appointment?.treatment?.rootId || '',
    [Field.APPOINTMENT_DATE]: appointment?.appointmentDate ?
      convertDateFormat(appointment.appointmentDate) : '',
    [Field.WANTS_SOONER]: appointment?.wantsSooner || false,
    [Field.OPERATORY_ID]: appointment?.operatory?.id || '',
    [Field.NEW_REFERRING_DOCTOR]: false
  };

  useEffect(() => {
    dispatch(setTitle(t('pageTitle')));
  }, []);

  const onSubmit = async (
      values,
      actions
  ) => {
    const {
      senecaPatientId,
      patientFirstName,
      patientLastName,
      patientId,
      doctorId,
      appointmentId,
      treatmentId,
      wantsSooner,
      operatoryId,
      newReferringDoctor
    } = sanitize(values);

    const requestBody = {
      patient: {
        id: senecaPatientId,
        firstName: patientFirstName,
        lastName: patientLastName,
        patientId: patientId
      },
      doctorId: doctorId ? doctorId : null,
      newReferringDoctor,
      appointment: appointment ? {
        id: appointmentId,
        treatmentId: treatmentId,
        wantsSooner: wantsSooner,
        operatoryId: operatoryId
      } : null
    };

    try {
      await updateCallSheetById(id, requestBody);
      actions.resetForm({values: values});
      addSuccessToast(t('toasts.updateCallSheetMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'call sheet');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const backToCallSheetList = useCallback(() => {
    history.push(callSheetsPathMemo || callSheetsRoutes.list());
  }, [callSheetsPathMemo]);

  const rescheduleAction = useCallback(async () => {
    if (!latestSchedulingProcess) return;

    setIsRescheduleProcessing(true);
    const patientFullName = `${patient?.firstName} ${patient?.lastName}`;

    try {
      await startSubProcess(latestSchedulingProcess.id, {
        processId: latestSchedulingProcess.id,
        subProcessType: Subprocess.RESCHEDULE_APPOINTMENT
      });

      backToCallSheetList();

      const toastMessage =
        getSubprocessToastMessage(
            Subprocess.RESCHEDULE_APPOINTMENT,
            processesNames.SCHEDULING,
            patientFullName
        );
      addSuccessToast(toastMessage);
    } catch (error) {
      addErrorToast(error.message);
    } finally {
      setIsRescheduleProcessing(false);
      setPopupData(null);
    }
  }, [latestSchedulingProcess, patient, id, backToCallSheetList]);

  const setRescheduleActionPopupData = useCallback(() => {
    const message = getSubprocessPopupMessage(
        Subprocess.RESCHEDULE_APPOINTMENT, processesNames.SCHEDULING
    );
    setPopupData(
        {
          labels: {
            body: message,
            confirmBtn: 'Confirm'
          },
          confirmableAction: rescheduleAction
        });
  }, [rescheduleAction]);


  const showRescheduleButton = useMemo(() => {
    const hasReschedulePermission =
    permissionsInfo.processes.reschedule_cancel.some(
        (permission) => currentUserPermissions.includes(permission)
    );

    const {startedSubProcesses, status, type} = latestSchedulingProcess || {};

    const rescheduleAppointmentNotIncluded =
    !startedSubProcesses?.includes(Subprocess.RESCHEDULE_APPOINTMENT);

    const isSchedulingProcess = type === ProcessType.SCHEDULING;
    const isRunningProcess = status === ProcessCode.RUNNING;

    return hasReschedulePermission && isSchedulingProcess &&
      isRunningProcess && rescheduleAppointmentNotIncluded &&
      appointment?.appointmentDate;
  }, [latestSchedulingProcess, currentUserPermissions, appointment]);


  return (
    <Container>
      <h2 className="subheader margin-top-25">
        {`${t('editForm.title')}${id}`}
      </h2>
      {isLoading ? <Spinner loadingText={t('editForm.loadingMessage')} /> :
      <Formik
        initialValues={initialValues}
        validationSchema={callSheetValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({
          touched, values, submitForm,
          isSubmitting, dirty, isValid, setFieldValue
        }) => {
          useOnUnsavedChanges(dirty);

          useEffect(() => {
            const shouldChangeValueToNewDoctor =
              newDoctorId && !dirty && !touched.doctorId;

            if (shouldChangeValueToNewDoctor) {
              setFieldValue(Field.DOCTOR_ID, newDoctorId);
            }
          }, []);

          const setNewReferringDoctorPopupData = useCallback(() => {
            setPopupData(
                {
                  labels: {
                    body: t('popups.newReferringDoctor.body'),
                    confirmBtn: t('popups.newReferringDoctor.confirmBtn')
                  },
                  confirmableAction: () => {
                    setFieldValue(Field.NEW_REFERRING_DOCTOR, true);
                    setPopupData(null);
                  }
                });
          }, []);

          return (
            <>
              <Form className="form">
                <p className="call-sheet-form-heading">{t('editForm.sections.patientData')}</p>
                <div className="creation-form-wrapper">
                  <FormGroup>
                    <TextField
                      required
                      name={Field.FIRST_NAME}
                      label={t('editForm.fields.firstName')}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => {
                        setError('');
                        setApiValidationError(null);
                        clearMessages();
                      }}
                      disabled={!hasEditPermission || isSubmitting}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      required
                      name={Field.LAST_NAME}
                      label={t('editForm.fields.lastName')}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => {
                        setError('');
                        setApiValidationError(null);
                        clearMessages();
                      }}
                      disabled={!hasEditPermission || isSubmitting}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      name={Field.SENECA_PATIENT_ID}
                      label={permissionsInfo.patients.view.some((permission) =>
                        currentUserPermissions.includes(permission)) ? (
                        <Link
                          className="link"
                          to={{
                            pathname: patientsRoutes.item(
                                values?.[Field.SENECA_PATIENT_ID]
                            ),
                            state: {
                              callSheetsPathMemo,
                              from: callSheetsRoutes.item(id)
                            }
                          }}
                        >
                          {t('editForm.fields.senecaPatientId')}
                        </Link>
                      ) : t('editForm.fields.senecaPatientId')}
                      disabled
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      name={Field.PATIENT_ID}
                      label={t('editForm.fields.patientId')}
                      disabled={!hasEditPermission || isSubmitting}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => {
                        setError('');
                        setApiValidationError(null);
                        clearMessages();
                      }}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>
                </div>

                {hasDoctorsViewPermission && (
                  <>
                    <p className="call-sheet-form-heading">{t('editForm.sections.referralData')}</p>
                    <div className="creation-form-wrapper">
                      <FormGroup>
                        <AutocompleteField
                          name={Field.DOCTOR_ID}
                          label={t('editForm.fields.referringDoctor')}
                          withNewEntity={{
                            redirectOptions: redirectToDoctors,
                            label: t('editForm.createNewDoctor'),
                            permission: permissionsInfo.doctors.create
                          }}
                          options={doctorOptions}
                          disabled={!hasEditPermission || isSubmitting}
                          onFocus={() => setApiValidationError(null)}
                          onBlur={() => setError('')}
                          apiValidationError={apiValidationError}
                        />
                      </FormGroup>

                      <FormGroup>
                        <CheckboxField
                          name={Field.NEW_REFERRING_DOCTOR}
                          label={t('editForm.fields.newReferringDoctor')}
                          onFocus={() => setApiValidationError(null)}
                          onBlur={() => setError('')}
                          onChange={(checked) => {
                            if (checked) {
                              setNewReferringDoctorPopupData();
                            } else {
                              setFieldValue(Field.NEW_REFERRING_DOCTOR, false);
                            }
                          }}
                          disabled={
                            !hasEditPermission ||
                            isSubmitting ||
                            !values.doctorId
                          }
                        />
                      </FormGroup>
                    </div>
                  </>
                )}

                {appointment && (
                  <>
                    <p className="call-sheet-form-heading">{t('editForm.sections.appointmentData')}</p>
                    <div className="creation-form-wrapper">
                      <FormGroup>
                        <TextField
                          name={Field.APPOINTMENT_ID}
                          label={t('editForm.fields.appointmentId')}
                          disabled
                        />
                      </FormGroup>

                      <FormGroup>
                        <AutocompleteField
                          name={Field.TREATMENT_ID}
                          label={t('editForm.fields.treatmentId')}
                          options={treatmentOptions}
                          disabled={!hasEditPermission || isSubmitting}
                          onFocus={() => setApiValidationError(null)}
                          onBlur={() => setError('')}
                          apiValidationError={apiValidationError}
                        />
                      </FormGroup>

                      <FormGroup className="appointment">
                        <TextField
                          name={Field.APPOINTMENT_DATE}
                          label={t('editForm.fields.appointmentDate')}
                          disabled
                        />
                        {showRescheduleButton &&
                        <div className="reschedule-button">
                          <IconButton onClick={setRescheduleActionPopupData}>
                            <AutorenewIcon className="icon" />
                          </IconButton>
                          <Button variant="text" label={t('editForm.buttons.reschedule')} onClick={setRescheduleActionPopupData}/>
                        </div>
                        }
                      </FormGroup>

                      <FormGroup>
                        <CheckboxField
                          name={Field.WANTS_SOONER}
                          label={t('editForm.fields.wantsSooner')}
                          onFocus={() => setApiValidationError(null)}
                          onBlur={() => setError('')}
                          disabled={!hasEditPermission || isSubmitting}
                        />
                      </FormGroup>

                      <FormGroup>
                        <AutocompleteField
                          name={Field.OPERATORY_ID}
                          label={t('editForm.fields.operatoryId')}
                          options={operatoriesOptions}
                          disabled={!hasEditPermission || isSubmitting}
                          onFocus={() => setApiValidationError(null)}
                          onBlur={() => setError('')}
                          apiValidationError={apiValidationError}
                        />
                      </FormGroup>
                    </div>
                  </>
                )}
              </Form>

              <ActionButtons>
                <EnsurePermission
                  elementPermissions={permissionsInfo.calls.edit}>
                  <ButtonWithLoader
                    type="submit"
                    onClick={submitForm}
                    testId="update-record"
                    disabled={!(isValid && dirty)}
                    isSubmitting={isSubmitting}
                    label={t('editForm.buttons.update')}
                  />
                </EnsurePermission>

                <Button
                  onClick={backToCallSheetList}
                  label={t('editForm.buttons.back')}
                  variant="outlined"
                  color="secondary"
                />
              </ActionButtons>
              {popupData &&
                <ConfirmationPopup
                  labels={{
                    body: popupData.labels.body,
                    confirmBtn: popupData.labels.confirmBtn
                  }}
                  isOpen={!!popupData}
                  setIsOpen={() => setPopupData(null)}
                  confirmableAction={popupData.confirmableAction}
                  isActionProcessing={isRescheduleProcessing}
                />
              }
            </>
          );
        }}
      </Formik>}

      <Error message={error} />
    </Container>
  );
};

export default EditCallSheet;
